import { useEffect, useReducer, useRef, useState } from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import bobPoint from "@/assets/bob-point.lottie?url";
import { HardHat } from "lucide-react";

const MESSAGES = [
  {
    lead: "Psst! Start buying",
    rest: "from our list of verified scrap lots — fresh photos, GST-checked yards, and escrow on every order. 👉",
  },
  {
    lead: "Fresh HMS & sponge iron",
    rest: "from 3,400+ GST-verified yards across India. AI-benchmarked pricing on every lot. 🔥",
  },
  {
    lead: "RBI-grade escrow",
    rest: "holds your payment until you inspect and approve the material. Zero trust issues. 🔒",
  },
  {
    lead: "MS billets, scrap turnings, shredded",
    rest: "— all photo-verified with weighbridge slips before dispatch. ✅",
  },
  {
    lead: "Live truck tracking",
    rest: "from yard to your foundry gate. No surprises, no middlemen. 🚛",
  },
  {
    lead: "Want to compare lots?",
    rest: "Browse today's verified inventory with AI-checked prices in one click. 📈",
  },
];

// Phase-based choreography. Character and bubble stay locked together.
// hidden -> char-in -> bubble-in -> hold -> exit (both leave together) -> hidden
const CHAR_IN_MS = 620;
const BUBBLE_IN_MS = 320;
const HOLD_MS = 4800;
const EXIT_MS = 520;       // unified exit duration for both character + bubble
const BETWEEN_MS = 700;    // gap while hidden before next message
const INITIAL_DELAY_MS = 450;

const CHAR_VISIBLE_Y = 0;
const CHAR_HIDDEN_Y = 110;

type Phase = "hidden" | "char-in" | "bubble-in" | "hold" | "exit";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export const HeroMascot = () => {
  const [failed, setFailed] = useState(false);
  const [phase, setPhase] = useState<Phase>("hidden");
  // msgIndex is only ever mutated while phase === "hidden", so text never
  // swaps mid-transition.
  const [msgIndex, setMsgIndex] = useState(0);
  const timerRef = useRef<number | null>(null);
  // Force a re-trigger key so React commits a fresh transition each cycle.
  const [, bump] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    const reduced = prefersReducedMotion();
    let cancelled = false;

    const schedule = (fn: () => void, ms: number) => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        if (!cancelled) fn();
      }, ms);
    };

    // Reduced-motion: just rotate copy with the character sitting still.
    if (reduced) {
      setPhase("hold");
      const tick = () => {
        setMsgIndex((i) => (i + 1) % MESSAGES.length);
        schedule(tick, HOLD_MS + 1500);
      };
      schedule(tick, HOLD_MS + 1500);
      return () => {
        cancelled = true;
        if (timerRef.current) window.clearTimeout(timerRef.current);
      };
    }

    const step = (next: Phase, idx?: number) => {
      if (cancelled) return;
      if (idx !== undefined) setMsgIndex(idx);
      setPhase(next);
      bump();
    };

    let i = 0;
    // kickoff
    schedule(() => {
      step("char-in", i);
      schedule(() => {
        step("bubble-in");
        schedule(() => {
          step("hold");
          schedule(loopExit, HOLD_MS);
        }, BUBBLE_IN_MS);
      }, CHAR_IN_MS);
    }, INITIAL_DELAY_MS);

    function loopExit() {
      step("exit");
      schedule(() => {
        step("hidden");
        schedule(() => {
          i = (i + 1) % MESSAGES.length;
          step("char-in", i);
          schedule(() => {
            step("bubble-in");
            schedule(() => {
              step("hold");
              schedule(loopExit, HOLD_MS);
            }, BUBBLE_IN_MS);
          }, CHAR_IN_MS);
        }, BETWEEN_MS);
      }, EXIT_MS);
    }

    return () => {
      cancelled = true;
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  const msg = MESSAGES[msgIndex] ?? MESSAGES[0];
  if (!msg) return null;

  const charVisible = phase === "char-in" || phase === "bubble-in" || phase === "hold";
  const bubbleVisible = phase === "bubble-in" || phase === "hold";
  // During "exit", both leave together using EXIT_MS.
  const isExiting = phase === "exit";

  const charDuration = isExiting ? EXIT_MS : CHAR_IN_MS;
  const bubbleDuration = isExiting ? EXIT_MS : BUBBLE_IN_MS;

  return (
    <div className="pointer-events-none relative h-[212px] w-[290px] overflow-hidden">
      {/* Speech bubble */}
      <div
        className="absolute bottom-[128px] right-0 max-w-[260px] origin-bottom-right rounded-2xl rounded-br-sm bg-nav-foreground/95 px-3.5 py-2.5 text-xs leading-snug text-nav shadow-xl ring-1 ring-nav-foreground/20"
        style={{
          transitionProperty: "opacity, transform",
          transitionDuration: `${bubbleDuration}ms`,
          transitionTimingFunction: isExiting
            ? "cubic-bezier(0.7, 0, 0.84, 0)"
            : "cubic-bezier(0.16, 1, 0.3, 1)",
          opacity: bubbleVisible && !isExiting ? 1 : 0,
          transform:
            bubbleVisible && !isExiting
              ? "translateY(0) scale(1)"
              : "translateY(14px) scale(0.96)",
        }}
        aria-hidden={!bubbleVisible || isExiting}
      >
        <p className="font-semibold">
          <span className="text-accent">{msg.lead}</span>
        </p>
        <p className="mt-0.5 text-nav/80">{msg.rest}</p>
        <span className="absolute -bottom-1.5 right-6 h-3 w-3 rotate-45 bg-nav-foreground/95" />
      </div>

      {/* Character */}
      <div
        className="absolute bottom-0 right-0 shrink-0"
        style={{
          transitionProperty: "transform",
          transitionDuration: `${charDuration}ms`,
          transitionTimingFunction:
            charVisible && !isExiting
              ? "cubic-bezier(0.16, 1, 0.3, 1)"
              : "cubic-bezier(0.7, 0, 0.84, 0)",
          width: 140,
          height: 140,
          transform:
            charVisible && !isExiting
              ? `translateY(${CHAR_VISIBLE_Y}%)`
              : `translateY(${CHAR_HIDDEN_Y}%)`,
          willChange: "transform",
        }}
      >
        {failed ? (
          <div className="flex h-full w-full items-center justify-center rounded-full bg-accent/20 ring-2 ring-accent/40">
            <HardHat className="h-10 w-10 text-warning" />
          </div>
        ) : (
          <DotLottieReact
            src={bobPoint}
            loop
            autoplay
            speed={0.5}
            style={{ width: "100%", height: "100%" }}
            onError={() => setFailed(true)}
          />
        )}
      </div>
    </div>
  );
};

export default HeroMascot;
